import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { T } from "../tokens";
import { Btn } from "../components/Btn";
import { PasswordField } from "../components/PasswordField";
import { PasswordStrengthMeter } from "../components/PasswordStrengthMeter";
import { AuthShell } from "../components/AuthShell";
import { meetsMinimum, passwordsMatch, MIN_PASSWORD_LENGTH } from "../lib/password-strength";

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "10px 12px", background: T.s2, border: `1px solid ${T.b1}`,
  borderRadius: "7px", color: T.t0, fontFamily: T.body, fontSize: "13px", outline: "none",
};
const cardStyle: React.CSSProperties = {
  width: "100%", padding: "32px", background: T.s1,
  border: `1px solid ${T.b0}`, borderRadius: "12px",
};
const shell = (children: React.ReactNode) => (
  <AuthShell><div style={cardStyle}>{children}</div></AuthShell>
);

export function ResetPassword() {
  const navigate = useNavigate();
  const [params]  = useSearchParams();
  const token     = params.get("token") ?? "";

  const [password, setPass]   = useState("");
  const [confirm,  setConfirm] = useState("");
  const [error, setError]     = useState<string | null>(null);
  const [busy, setBusy]       = useState(false);
  const [done, setDone]       = useState(false);

  const tooShort = password.length > 0 && !meetsMinimum(password);
  const mismatch = confirm.length > 0 && !passwordsMatch(password, confirm);
  const ready    = meetsMinimum(password) && passwordsMatch(password, confirm) && !busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!ready) {
      setError(!meetsMinimum(password) ? `Password must be at least ${MIN_PASSWORD_LENGTH} characters` : "Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await axios.post("/api/auth/reset-password", { token, newPassword: password });
      setDone(true);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? "This reset link is invalid or has expired. Request a new one.");
    } finally { setBusy(false); }
  }

  // Missing token → the link is malformed; send them to request a fresh one.
  if (!token) {
    return shell(
      <div style={{ textAlign: "center" }}>
        <h1 style={{ fontFamily: T.display, fontSize: "20px", fontWeight: 700, color: T.t0, margin: "0 0 8px" }}>Invalid reset link</h1>
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, lineHeight: 1.6, margin: "0 0 20px" }}>
          This link is missing its token. Request a new password reset.
        </p>
        <Btn variant="cta" size="md" onClick={() => navigate("/forgot-password")}>Request a new link</Btn>
      </div>,
    );
  }

  if (done) {
    return shell(
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "34px", marginBottom: "8px" }}>✅</div>
        <h1 style={{ fontFamily: T.display, fontSize: "20px", fontWeight: 700, color: T.t0, margin: "0 0 8px" }}>Password updated</h1>
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, lineHeight: 1.6, margin: "0 0 20px" }}>
          Your password has been reset. Sign in with your new password.
        </p>
        <Btn variant="cta" size="md" onClick={() => navigate("/login")}>Sign in</Btn>
      </div>,
    );
  }

  return shell(
    <>
      <div style={{ marginBottom: "20px" }}>
        <h1 style={{ fontFamily: T.display, fontSize: "22px", fontWeight: 700, color: T.t0, margin: 0 }}>Set a new password</h1>
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, marginTop: "6px" }}>Choose a strong password you don't use elsewhere.</p>
      </div>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <PasswordField value={password} onChange={setPass}
          placeholder={`New password (min ${MIN_PASSWORD_LENGTH} chars)`} autoComplete="new-password" required style={inputStyle} />
        <PasswordStrengthMeter password={password} />
        <PasswordField value={confirm} onChange={setConfirm}
          placeholder="Confirm new password" autoComplete="new-password" required style={inputStyle} />
        {mismatch && <span style={{ fontFamily: T.body, fontSize: "11px", color: T.err }}>Passwords do not match</span>}
        {tooShort && !mismatch && <span style={{ fontFamily: T.body, fontSize: "11px", color: T.err }}>Password must be at least {MIN_PASSWORD_LENGTH} characters</span>}
        {error && <p style={{ fontFamily: T.body, fontSize: "12px", color: T.err, margin: 0 }}>{error}</p>}
        <Btn type="submit" variant="cta" size="md" disabled={!ready}>{busy ? "Updating…" : "Update password"}</Btn>
      </form>
    </>,
  );
}
